/** Roles & permissions page (/w/:ws/settings/roles) and the Team members tab (P3 scope). */
export const roles = {
  title: "Roles & permissions",
  subtitle: "Decide what each role can do, scope it to agents or teams, and assign it to members.",
  backToTeam: "Team",
  newRole: "New role",
  listLabel: "Roles",
  builtIn: "Built-in",
  custom: "Custom",
  locked: "Locked",
  lockedHint: "Built-in roles can't be edited. Duplicate one to customize it.",
  members_one: "{{count}} member",
  members_other: "{{count}} members",
  permissions_one: "{{count}} permission",
  permissions_other: "{{count}} permissions",
  duplicate: "Duplicate",
  duplicateName: "{{name}} (copy)",
  duplicated: "Role duplicated — rename it and adjust permissions",
  delete: "Delete role",
  emptyTitle: "Pick a role",
  emptyBody: "Select a role to see its permissions, or create a new one.",
  loadError: "Roles couldn't load.",
  notFound: "That role doesn't exist anymore.",
  editor: {
    createTitle: "Create role",
    editTitle: "Edit role",
    viewTitle: "{{name}}",
    name: "Role name",
    namePlaceholder: "e.g. Front desk",
    nameRequired: "Give the role a name",
    description: "Description",
    descriptionPlaceholder: "What is this role for?",
    startFrom: "Start from",
    startBlank: "Blank role",
    startFromRole: "Copy of {{name}}",
    save: "Save role",
    create: "Create role",
    cancel: "Cancel",
    discard: "Discard changes",
    saved: "Role saved",
    created: "Role “{{name}}” created",
    unsaved: "Unsaved changes",
    auditNote: "Every change to roles and assignments is recorded in the audit log.",
  },
  matrix: {
    title: "Permissions",
    subtitle: "Modules down the side, actions across the top. “—” means the action doesn't exist for that module.",
    module: "Module",
    unavailable: "Not available for this module",
    toggle: "{{module}}: {{action}}",
    selectRow: "All {{module}} permissions",
    notHeld: "You can't grant a permission you don't hold",
    legendAllowed: "Allowed",
    legendNone: "Not allowed",
    legendNa: "Doesn't apply",
  },
  modules: {
    workspace: "Workspace",
    agents: "Agents",
    playground: "Playground",
    knowledge: "Knowledge",
    actions: "Actions",
    evaluations: "Evaluations",
    widget: "Widget",
    channels: "Channels",
    inbox: "Inbox",
    leads: "Leads",
    gaps: "Knowledge gaps",
    announcements: "Announcements",
    analytics: "Analytics",
    audiences: "Audiences",
    team: "Team",
    roles: "Roles",
    billing: "Billing",
    settings: "Settings",
    api_keys: "API keys",
    webhooks: "Webhooks",
    integrations: "Integrations",
    audit: "Audit log",
    data: "Data",
    tickets: "Tickets",
    organisations: "Organisations",
    assets: "Assets",
    visits: "Field visits",
    orders: "Orders",
    connectors: "Connectors",
    customers: "Customers",
  },
  actions: {
    view: "View",
    create: "Create",
    edit: "Edit",
    delete: "Delete",
    publish: "Publish",
    use: "Use",
    reindex: "Re-index",
    manage: "Manage",
    run: "Run",
    reply: "Reply",
    takeover: "Take over",
    assign: "Assign",
    export: "Export",
    resolve: "Resolve",
    send: "Send",
    invite: "Invite",
    note: "Note",
    configure: "Configure",
    reports: "Reports",
    perform: "Perform",
    approve: "Approve",
    view_reference: "View reference",
    manage_launch: "Manage secure launch",
    publish_override: "Publish override",
  },
  effective: {
    title: "Effective permissions",
    subtitle: "What this role grants once saved, checked by the server.",
    memberTitle: "Effective permissions for {{name}}",
    memberSubtitle: "Union of every assignment below, for the selected scope.",
    count: "{{granted}} of {{total}} permissions",
    loading: "Checking with the server…",
    error: "The preview couldn't load. You can still save.",
    previewFor: "Preview for",
    anyScope: "Workspace (all agents)",
    forAgent: "Agent: {{name}}",
    forTeam: "Team: {{name}}",
    none: "This role grants no permissions yet.",
  },
  scope: {
    title: "Scope",
    all: "All agents",
    allHint: "Applies everywhere in the workspace.",
    agents: "Specific agents",
    agentsHint: "Only for conversations, knowledge and settings of the chosen agents.",
    teams: "Specific teams",
    teamsHint: "Only for handoffs routed to the chosen teams.",
    chooseAgents: "Choose agents",
    chooseTeams: "Choose teams",
    searchAgents: "Search agents…",
    searchTeams: "Search teams…",
    noAgents: "No agents yet",
    noTeams: "No teams yet",
    selected_one: "{{count}} selected",
    selected_other: "{{count}} selected",
    agentsUnavailable: "The agent list isn't available yet — scope by team or apply to all agents for now.",
    pickOne: "Choose at least one",
    summaryAll: "All agents",
    summaryAgents_one: "{{count}} agent",
    summaryAgents_other: "{{count}} agents",
    summaryTeams_one: "{{count}} team",
    summaryTeams_other: "{{count}} teams",
  },
  assign: {
    title: "Members with this role",
    empty: "No one has this role yet.",
    add: "Assign to member",
    dialogTitle: "Assign {{role}}",
    dialogSubtitle: "The member keeps their other roles. Effective permissions are the union of all assignments.",
    member: "Member",
    chooseMember: "Choose a member",
    searchMembers: "Search members…",
    noMembers: "No members found",
    submit: "Assign role",
    assigned: "{{role}} assigned to {{name}}",
    remove: "Remove from role",
    removed: "{{role}} removed from {{name}}",
    you: "(you)",
    ownRole: "You can't change your own roles",
  },
  deleteDialog: {
    title: "Delete “{{name}}”?",
    body: "Members lose the permissions this role grants. This can't be undone.",
    inUse_one: "{{count}} member has this role. Choose a role to move them to first.",
    inUse_other: "{{count}} members have this role. Choose a role to move them to first.",
    reassignTo: "Move members to",
    chooseRole: "Choose a role",
    submit: "Delete role",
    deleted: "Role deleted",
  },
  planGate: {
    badge: "Pro plan",
    title: "Custom roles are a Pro feature",
    body: "You're on {{plan}}, which includes the five built-in roles: Owner, Admin, Editor, Agent and Viewer. Upgrade to Pro or Institution to create roles, scope them to agents or teams, and assign them to members.",
    upgrade: "Compare plans",
    builtInsBelow: "Built-in roles are listed below for reference.",
  },
  guardrails: {
    grantNotHeld: "You can't grant permissions you don't hold.",
    selfRoleEdit: "You can't change your own role. Ask another Owner or Admin.",
    lastOwner: "A workspace must always have at least one Owner.",
    roleLocked: "Built-in roles can't be changed or deleted.",
    roleInUse: "This role is assigned. Choose a role to reassign its members to first.",
    conflict: "A role with this name already exists.",
    readOnly: "This session is read-only.",
  },
} as const;
